import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NODE_ENV: z
      .enum(["development", "test", "production"])
      .default("development"),
    APP_ENV: z.string().optional(),
    BETTER_AUTH_SECRET: z.string(),
    GOOGLE_CLIENT_ID: z.string().optional(),
    GOOGLE_CLIENT_SECRET: z.string().optional(),
    DATABASE_URL: z.string(),
    BASIC_AUTH: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    AZURE_OPENAI_API_KEY: z.string().optional(),
    AZURE_OPENAI_ENDPOINT: z.string().optional(),
    AZURE_OPENAI_MODEL_DEPLOYMENT: z.string().optional(),
    DEEPSEEK_API_KEY: z.string().optional(),
    ANTHROPIC_API_KEY: z.string().optional(),
    GEMINI_API_KEY: z.string().optional(),
    UPLOADTHING_TOKEN: z.string(),
    VERCEL_URL: z.string().optional(),
    REDIS_HOST: z.string().optional(),
    REDIS_PORT: z.coerce.number().optional(),
    REDIS_USERNAME: z.string().optional(),
    REDIS_PASSWORD: z.string().optional(),
    REDIS_DB: z.string().default("0"),
    REDIS_PREFIX: z.string().default("luregod"),
    STRIPE_SECRET_KEY: z.string(),
    PAYPAL_CLIENT_ID: z.string().optional(),
    PAYPAL_CLIENT_SECRET: z.string(),
    PAYPAL_API_URL: z.string().default("https://api-m.sandbox.paypal.com"),
    MAILGUN_API_URL: z.string().optional(),
    MAILGUN_API_KEY: z.string(),
    MAILGUN_DOMAIN: z.string(),
    MAILGUN_FROM_EMAIL: z.string(),
    SINCH_API_TOKEN: z.string().optional(),
    SINCH_SERVICE_ID: z.string().optional(),
    SINCH_FROM_NUMBER: z.string().optional(),
    MESSAGE_MEDIA_API_KEY: z.string().optional(),
    MESSAGE_MEDIA_API_SECRET: z.string().optional(),
    SENTRY_DSN: z.string().optional(),
    MEILISEARCH_HOST: z.string().optional().default("http://localhost:7700"),
    MEILISEARCH_API_KEY: z.string().optional().optional().default(""),
    MEILISEARCH_INDEX_PREFIX: z.string().optional().default("luregod_"),
    SMARTPROXY_URL: z.string().optional(),
    TURNSTILE_SECRET_KEY: z.string(),
    GOOGLE_MAPS_API_KEY: z.string(),
    THUMBOR_URL: z.string().default("https://thumbor.luregod.com"),
    THUMBOR_KEY: z.string().optional(),
    LABRAN_API_KEY: z.string().optional(),
    MERIT_API_KEY: z.string().optional(),
    MERIT_API_SECRET: z.string().optional(),
    ENCRYPTION_KEY: z.string(),
    META_CONVERSIONS_ACCESS_TOKEN: z.string(),
    MUX_TOKEN_ID: z.string(),
    MUX_TOKEN_SECRET: z.string(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_APP_ENV: z.string().optional(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_APP_URL: z.string(),
    NEXT_PUBLIC_MAIN_APP_URL: z.string().optional(),
    NEXT_PUBLIC_ADMIN_URL: z.string().optional(),
    NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: z.string(),
    NEXT_PUBLIC_POSTHOG_KEY: z.string(),
    NEXT_PUBLIC_POSTHOG_HOST: z.string(),
    NEXT_PUBLIC_DOMAIN: z.string().optional(),
    NEXT_PUBLIC_DOMAIN_EE: z.string().optional(),
    NEXT_PUBLIC_DOMAIN_DE: z.string().optional(),
    NEXT_PUBLIC_DOMAIN_NL: z.string().optional(),
    NEXT_PUBLIC_DOMAIN_BE: z.string().optional(),
    NEXT_PUBLIC_CRISP_WEBSITE_ID: z
      .string()
      .optional()
      .default("df76501f-5f67-4391-8864-4a224e20f31a"),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    APP_ENV: process.env.APP_ENV,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_MAIN_APP_URL:
      process.env.NEXT_PUBLIC_MAIN_APP_URL ?? process.env.NEXT_PUBLIC_APP_URL,
    NODE_ENV: process.env.NODE_ENV,
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,
    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    DATABASE_URL: process.env.DATABASE_URL,
    BASIC_AUTH: process.env.BASIC_AUTH,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    AZURE_OPENAI_API_KEY: process.env.AZURE_OPENAI_API_KEY,
    AZURE_OPENAI_ENDPOINT: process.env.AZURE_OPENAI_ENDPOINT,
    AZURE_OPENAI_MODEL_DEPLOYMENT: process.env.AZURE_OPENAI_MODEL_DEPLOYMENT,
    DEEPSEEK_API_KEY: process.env.DEEPSEEK_API_KEY,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    GEMINI_API_KEY: process.env.GEMINI_API_KEY,
    VERCEL_URL: process.env.VERCEL_URL,
    UPLOADTHING_TOKEN: process.env.UPLOADTHING_TOKEN,
    REDIS_HOST: process.env.REDIS_HOST,
    REDIS_PORT: process.env.REDIS_PORT,
    REDIS_USERNAME: process.env.REDIS_USERNAME,
    REDIS_PASSWORD: process.env.REDIS_PASSWORD,
    REDIS_DB: process.env.REDIS_DB,
    REDIS_PREFIX: process.env.REDIS_PREFIX,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    PAYPAL_CLIENT_ID: process.env.PAYPAL_CLIENT_ID,
    PAYPAL_CLIENT_SECRET: process.env.PAYPAL_CLIENT_SECRET,
    PAYPAL_API_URL: process.env.PAYPAL_API_URL,
    MAILGUN_API_KEY: process.env.MAILGUN_API_KEY,
    MAILGUN_API_URL: process.env.MAILGUN_API_URL,
    MAILGUN_DOMAIN: process.env.MAILGUN_DOMAIN,
    MAILGUN_FROM_EMAIL: process.env.MAILGUN_FROM_EMAIL,
    SINCH_API_TOKEN: process.env.SINCH_API_TOKEN,
    SINCH_SERVICE_ID: process.env.SINCH_SERVICE_ID,
    SINCH_FROM_NUMBER: process.env.SINCH_FROM_NUMBER,
    MESSAGE_MEDIA_API_KEY: process.env.MESSAGE_MEDIA_API_KEY,
    MESSAGE_MEDIA_API_SECRET: process.env.MESSAGE_MEDIA_API_SECRET,
    SENTRY_DSN: process.env.SENTRY_DSN,
    MEILISEARCH_HOST: process.env.MEILISEARCH_HOST,
    MEILISEARCH_API_KEY: process.env.MEILISEARCH_API_KEY,
    MEILISEARCH_INDEX_PREFIX: process.env.MEILISEARCH_INDEX_PREFIX,
    SMARTPROXY_URL: process.env.SMARTPROXY_URL,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_ADMIN_URL:
      process.env.NEXT_PUBLIC_ADMIN_URL ?? process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
    TURNSTILE_SECRET_KEY: process.env.TURNSTILE_SECRET_KEY,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_CRISP_WEBSITE_ID: process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID,
    GOOGLE_MAPS_API_KEY: process.env.GOOGLE_MAPS_API_KEY,
    NEXT_PUBLIC_DOMAIN: process.env.NEXT_PUBLIC_DOMAIN,
    NEXT_PUBLIC_DOMAIN_EE: process.env.NEXT_PUBLIC_DOMAIN_EE,
    NEXT_PUBLIC_DOMAIN_DE: process.env.NEXT_PUBLIC_DOMAIN_DE,
    NEXT_PUBLIC_DOMAIN_NL: process.env.NEXT_PUBLIC_DOMAIN_NL,
    NEXT_PUBLIC_DOMAIN_BE: process.env.NEXT_PUBLIC_DOMAIN_BE,
    THUMBOR_URL: process.env.THUMBOR_URL,
    THUMBOR_KEY: process.env.THUMBOR_KEY,
    LABRAN_API_KEY: process.env.LABRAN_API_KEY,
    MERIT_API_KEY: process.env.MERIT_API_KEY,
    MERIT_API_SECRET: process.env.MERIT_API_SECRET,
    ENCRYPTION_KEY: process.env.ENCRYPTION_KEY,
    META_CONVERSIONS_ACCESS_TOKEN: process.env.META_CONVERSIONS_ACCESS_TOKEN,
    MUX_TOKEN_ID: process.env.MUX_TOKEN_ID,
    MUX_TOKEN_SECRET: process.env.MUX_TOKEN_SECRET,
  },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,

  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
